import { useScaffold } from "@Light/scaffold";
import {
  formatChargingSchedule,
  formatOnScheduleRate,
} from "@Light/utils/evProgram";
import { usePlan } from "@Light/utils/plan";
import { useWidth } from "@Light/utils/width";
import clsx from "clsx";
import { ChartDatum } from "./chartData";

import { Tooltip } from "react-tooltip";

export type IntervalPercentagesProps = {
  data: ChartDatum[];
};

export const IntervalPercentages: React.FC<IntervalPercentagesProps> = ({
  data,
}) => {
  const plan = usePlan();
  const { vehicleCharging } = plan.features;
  const consumptionTotal = data.reduce(
    (acc, datum) => acc + datum.consumption,
    0.0,
  );
  const vehicleChargingTotal = data.reduce(
    (acc, datum) => acc + datum.vehicle_charging,
    0.0,
  );
  const eligibleVehicleChargingTotal = data.reduce(
    (acc, datum) => acc + datum.eligible_vehicle_charging,
    0.0,
  );
  if (vehicleCharging === undefined) {
    return null;
  }

  const schedule = formatChargingSchedule(vehicleCharging.schedules);
  const rate = formatOnScheduleRate(vehicleCharging.onScheduleRates);

  return (
    <div className="flex flex-col gap-2">
      <Item
        label="Home"
        percentage={
          ((consumptionTotal - vehicleChargingTotal) / consumptionTotal) * 100
        }
        color="#8ED1D9"
      />
      <Item
        label={rate ? "" : "Vehicle charging (discounted)"}
        tooltipLabel={rate && `Vehicle charging (${rate})`}
        tooltipId="interval-percentages-on-schedule-tooltip"
        tooltip={
          rate ? (
            <>
              EV charging available for {rate}:
              <br />
              {schedule}
            </>
          ) : (
            schedule
          )
        }
        percentage={(eligibleVehicleChargingTotal / consumptionTotal) * 100}
        color="#1570EF"
      />
      <Item
        label="Vehicle charging (normal rate)"
        percentage={
          ((vehicleChargingTotal - eligibleVehicleChargingTotal) /
            consumptionTotal) *
          100
        }
        color="#C07A79"
      />
    </div>
  );
};

export type ItemProps = {
  label: string;
  percentage: number;
  color: string;
  /** Appended to the label, and the only part of it that anchors the tooltip. */
  tooltipLabel?: React.ReactNode;
  tooltipId?: string;
  tooltip?: React.ReactNode;
};

export const Item: React.FC<ItemProps> = ({
  label,
  percentage,
  color,
  tooltipLabel,
  tooltipId,
  tooltip,
}) => {
  const [ref, width] = useWidth<HTMLDivElement>();
  const barWidth = (width * percentage) / 100;
  const scaffold = useScaffold();
  return (
    <div className="flex flex-col">
      <div className={clsx("text-sm", scaffold.page.colors.text.secondary)}>
        {label}
        {tooltipLabel && (
          <>
            {label && " "}
            <span
              data-tooltip-id={tooltipId}
              className="underline decoration-slate-400 decoration-dotted cursor-help"
            >
              {tooltipLabel}
            </span>
          </>
        )}
      </div>
      {tooltipId && (
        <Tooltip id={tooltipId} place="top">
          <div className="text-xs text-center whitespace-nowrap">{tooltip}</div>
        </Tooltip>
      )}
      <div className="flex flex-row gap-2 items-center">
        <div
          ref={ref}
          className="rounded-xl h-2 w-full"
          style={{ background: "#EAECF0" }}
        >
          <div
            className="rounded-xl h-2"
            style={{
              background: color,
              width: barWidth,
            }}
          />
        </div>
        <div
          className={clsx("text-sm w-10", scaffold.page.colors.text.secondary)}
        >
          {Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(
            percentage,
          )}
          %
        </div>
      </div>
    </div>
  );
};
